import { useState } from "react";
import PinnedFiltersWidget from "@/components/admin/PinnedFiltersWidget";
import RxSeriesEfficiencyCard from "@/components/admin/RxSeriesEfficiencyCard";
import PatientTourStatsCard from "@/components/admin/PatientTourStatsCard";
import BAAComplianceWidget from "@/components/admin/BAAComplianceWidget";
import WaitlistedStatesCard from "@/components/admin/WaitlistedStatesCard";
import LandingFlagsCard from "@/components/admin/LandingFlagsCard";
import MyProfileCompletionCard from "@/components/admin/MyProfileCompletionCard";
import PatientDetailModal from "@/components/PatientDetailModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PasswordChangeCard } from "@/components/prescriber/EmployeeProfileExtras";
import BundleSizeReportCard from "@/components/admin/BundleSizeReportCard";
import BotAnalyticsCard from "@/components/admin/BotAnalyticsCard";
import GA4OverviewCard from "@/components/admin/GA4OverviewCard";
import BackupCard from "@/components/admin/BackupCard";
import { KeyRound } from "lucide-react";

const STAT_CARDS = [
  { key: "total_patients",     label: "Patients",       tab: "users" },
  { key: "total_prescribers",  label: "Prescribers",    tab: "users" },
  { key: "pending_orders",     label: "Pending Orders", tab: "orders" },
  { key: "total_payments",     label: "Payments",       tab: "orders" },
];

function statusBadgeClass(status) {
  if (status === "approved") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (status === "denied") return "bg-red-50 text-red-700 border-red-200";
  return "bg-amber-50 text-amber-700 border-amber-200";
}

export default function AdminOverviewTab({ stats, orders, staffUsers, setActiveTab }) {
  const [openPatientId, setOpenPatientId] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  return (
    <div data-testid="admin-overview">
      <PinnedFiltersWidget onJump={(tabKey) => setActiveTab(tabKey)} />
      <div className="mb-6">
        <MyProfileCompletionCard onJump={(tabKey) => setActiveTab(tabKey)} />
      </div>
      {/* Quick-access account security card — one tap to rotate your admin password. */}
      <div className="mb-6">
        <button
          type="button"
          onClick={() => setPwOpen(true)}
          className="w-full text-left rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 hover:border-purple-400 dark:hover:border-purple-500 transition-colors flex items-center gap-3"
          data-testid="admin-change-pw-card"
        >
          <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-purple-600 dark:text-purple-300" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-900 dark:text-slate-100">Change your password</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Rotate anytime — logs out other sessions and clears the &quot;must change&quot; flag.</div>
          </div>
          <span className="text-xs font-semibold text-purple-600 dark:text-purple-300">Open →</span>
        </button>
      </div>
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="max-w-md" data-testid="admin-change-pw-modal">
          <DialogHeader>
            <DialogTitle>Change your password</DialogTitle>
          </DialogHeader>
          <PasswordChangeCard />
        </DialogContent>
      </Dialog>
      <div className="mb-6">
        <GA4OverviewCard />
      </div>
      <div className="mb-6">
        <BackupCard />
      </div>
      <div className="mb-6">
        <BundleSizeReportCard />
      </div>
      <div className="mb-6">
        <BotAnalyticsCard onJump={(tabKey) => setActiveTab(tabKey)} />
      </div>
      <div className="mb-6">
        <RxSeriesEfficiencyCard />
      </div>
      {/* Patient tour completion stats — self-hides when no tours have been
          recorded yet (fresh installs / before the first patient registers). */}
      <div className="mb-6">
        <PatientTourStatsCard />
      </div>
      {/* Top waitlisted states — self-hides when zero pending signups. */}
      <div className="mb-6">
        <WaitlistedStatesCard onJump={(tabKey) => setActiveTab(tabKey)} />
      </div>
      {/* Landing-page marketing module toggles. */}
      <div className="mb-6">
        <LandingFlagsCard />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {STAT_CARDS.map((s) => (
          <button
            key={s.label}
            type="button"
            onClick={() => s.tab && setActiveTab(s.tab)}
            className="text-left bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5 hover:border-purple-400 hover:shadow-md transition-all cursor-pointer"
            data-testid={`admin-stat-${s.key}`}
            title={s.tab ? `Open ${s.label}` : ""}
          >
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{s.label}</p>
            <p className="text-3xl font-bold text-slate-900 dark:text-white">{stats?.[s.key] || 0}</p>
          </button>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Recent Orders</h3>
          {orders.slice(0, 5).map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => setOpenPatientId(o.patient_id)}
              className="w-full text-left flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800/60 last:border-0 hover:bg-purple-50/40 dark:hover:bg-purple-900/10 px-2 -mx-2 rounded-md transition-colors"
              data-testid={`recent-order-${o.id}`}
              title={`Open ${o.patient_name}'s profile`}
            >
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-white hover:underline">{o.patient_name}</p>
                <p className="text-xs text-slate-400 dark:text-slate-500">{o.medication_name}</p>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full border ${statusBadgeClass(o.status)}`}>
                {o.status.replace(/_/g, " ")}
              </span>
            </button>
          ))}
        </div>
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Staff Accounts</h3>
          {staffUsers.map((u) => (
            <div key={u.id} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800/60 last:border-0">
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-white">{u.name}</p>
                <p className="text-xs text-slate-400 dark:text-slate-500">{u.employee_id || "N/A"} &middot; {u.prescriber_type || u.role}</p>
              </div>
              <span className={`text-xs font-medium ${u.is_active ? "text-emerald-600" : "text-red-500"}`}>
                {u.is_active ? "Active" : "Inactive"}
              </span>
            </div>
          ))}
        </div>
      </div>
      <BAAComplianceWidget onNavigate={() => setActiveTab("baa")} />
      <PatientDetailModal
        open={!!openPatientId}
        onClose={() => setOpenPatientId(null)}
        patientId={openPatientId}
      />
    </div>
  );
}
